import React from 'react';
import PropTypes from 'prop-types';

/**
 * The step counter, segmented track, and the live region that announces step
 * changes.
 *
 * Advancing swaps the step content without moving focus, so the change is
 * announced through `role="status"` rather than being left silent. The track
 * itself is aria-hidden — it is a duplicate of the counter for sighted users, and
 * the progressbar role above already carries the numeric state.
 */
const WalkthroughProgress = ({ steps, stepIndex }) => {
  const currentStep = steps[stepIndex];
  const position = `Step ${stepIndex + 1} of ${steps.length}`;

  return (
    <>
      <div
        className="walkthrough-progress"
        role="progressbar"
        aria-label={`Setup progress: ${currentStep}`}
        aria-valuemin="1"
        aria-valuemax={steps.length}
        aria-valuenow={stepIndex + 1}
      >
        <div className="walkthrough-progress__meta">
          <span className="walkthrough-progress__current">{currentStep}</span>
          <span>{position}</span>
        </div>
        <div className="walkthrough-progress__track" aria-hidden="true">
          {steps.map((step, index) => (
            <span
              key={step}
              className={[
                'walkthrough-progress__segment',
                index < stepIndex ? 'is-complete' : '',
                index === stepIndex ? 'is-current' : '',
              ].filter(Boolean).join(' ')}
            />
          ))}
        </div>
      </div>
      <div className="walkthrough-step-announcer" role="status" aria-live="polite">
        {`${position}: ${currentStep}`}
      </div>
    </>
  );
};

WalkthroughProgress.defaultProps = {
  steps: [],
  stepIndex: 0,
};

WalkthroughProgress.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.string),
  stepIndex: PropTypes.number,
};

export default WalkthroughProgress;
